import React, { useMemo } from "react";
import { useQuery } from "urql";

import { Grid, Column } from "../../../../components/src";
import { SWAP_CURVE_DEFINITION_ID } from "./constants";
import { GET_FONAV_CURVE } from "./queries";
import {
    FoNavInternalRateInput,
    FoNavSebComparisonRow,
    FoNavSebRateInput,
    buildFoNavSebSwapComparisonRows,
    formatFoNavSebAsOf,
    getFixedLegMaturityPeriod,
    getTimeSeriesValueAtDate,
    maturityPeriodToYears
} from "./utils";

const SEB_RATES_URL = "https://sebgroup.com/our-offering/rates-and-iban/swap-rates";

interface FoNavSebSwapsProps {
    endDate: string;
    pastedText: string;
    sebRates: FoNavSebRateInput[];
    onPastedTextChange: (value: string) => void;
}

export function FoNavSebSwaps({ endDate, pastedText, sebRates, onPastedTextChange }: FoNavSebSwapsProps): React.ReactElement {
    const [{ fetching, error, data }] = useQuery({
        query: GET_FONAV_CURVE,
        variables: {
            curveId: SWAP_CURVE_DEFINITION_ID,
            startDate: endDate,
            endDate
        },
        pause: !endDate,
        requestPolicy: "network-only"
    });

    const internalRates = useMemo((): FoNavInternalRateInput[] => {
        const instruments = data?.curveDefinition?.definition?.instruments;
        if (!instruments) {
            return [];
        }

        return instruments
            .map((item): FoNavInternalRateInput | null => {
                const instrument = item.instrument;
                if (!instrument) {
                    return null;
                }

                const maturityPeriod = getFixedLegMaturityPeriod(instrument.model?.legs);
                const raw = getTimeSeriesValueAtDate(instrument.endSeries, endDate);

                let ratePercent: number | null = null;
                if (raw !== null) {
                    ratePercent = raw * 100;
                }

                return {
                    maturityYears: maturityPeriod ? maturityPeriodToYears(maturityPeriod) : null,
                    ratePercent
                };
            })
            .filter((row): row is FoNavInternalRateInput => row !== null);
    }, [data, endDate]);

    const tableRows = useMemo((): FoNavSebComparisonRow[] => {
        return buildFoNavSebSwapComparisonRows(sebRates, internalRates);
    }, [sebRates, internalRates]);

    const asOf = useMemo(() => formatFoNavSebAsOf(sebRates), [sebRates]);

    const pasteFailed = pastedText.trim() !== "" && sebRates.length === 0;

    return (
        <div>
            <div className="mb-2">
                <label className="block text-[9pt] text-slate-600 mb-1" htmlFor="seb-swap-paste">
                    Paste the swap rate table from an external independent source, for example{" "}
                    <a href={SEB_RATES_URL} target="_blank" rel="noopener noreferrer" className="underline">
                        SEB
                    </a>
                </label>
                <textarea
                    id="seb-swap-paste"
                    value={pastedText}
                    onChange={(event) => onPastedTextChange(event.target.value)}
                    rows={4}
                    spellCheck={false}
                    placeholder={"Maturity\tPrice\t+/-\tTime\tDate\n1 Yr\t2.41\t0.00\t13:43\t25/08/2026"}
                    className="w-full font-mono text-[9pt] border border-slate-300 rounded p-2"
                />
                <div className="flex items-center justify-between mt-1">
                    <span className="text-[9pt] text-slate-500">
                        {sebRates.length > 0 ? `${sebRates.length} tenors read${asOf ? `, quoted ${asOf}` : ""}` : ""}
                        {pasteFailed ? "Could not read any tenors from that text" : ""}
                    </span>
                    {pastedText !== "" ? (
                        <button type="button" className="text-[9pt] underline text-slate-600" onClick={() => onPastedTextChange("")}>
                            Clear
                        </button>
                    ) : null}
                </div>
            </div>

            {fetching ? <p>Loading curve...</p> : null}
            {error ? <p>error: {JSON.stringify(error, null, 2)}</p> : null}

            {!fetching && !error ? (
                <Grid
                    header="SEK swap rates from external independent source"
                    data={tableRows}
                    className="table-timeseries"
                    tableClassName="table-xs"
                    sortable
                >
                    <Column field="tenor" title="Tenor" className="nowrap left" />
                    <Column field="sebRate" title="External (%)" className="nowrap left" />
                    <Column field="sebChange" title="External change (bp)" />
                    <Column field="internalRate" title="Internal (%)" />
                    <Column field="diff" title="Internal - External (bp)" />
                </Grid>
            ) : null}

            <p className="text-[9pt] text-slate-500 mt-1">
                External reference pasted by hand. Internal rates are the {endDate} close of our own SEK swap curve. For comparison
                only, not a valuation source.
            </p>
        </div>
    );
}
